import * as React from 'react';
import type {ReactNode} from 'react';
import {ChevronDown} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {cn} from '../../lib/cn';
import type {ControlProps} from '../_select-internals';
import type {SelectValue} from '../_select-internals';

type CountTriggerValueText =
  | string
  | ((selectedCount: number, total: number) => string)
  | undefined;

type CountTriggerOptions = {
  getValueText: () => CountTriggerValueText;
  getTotalCount: () => number | undefined;
};

function createCountTrigger(opts: CountTriggerOptions) {
  const {getValueText, getTotalCount} = opts;

  function CountTrigger<T, V extends SelectValue, L extends ReactNode>(
    props: ControlProps<T, V, L>,
  ) {
    const {t} = useTranslation();
    const {
      triggerId,
      listboxId,
      labelId,
      valueId,
      describedErrorId,
      isOpen,
      isBlocked,
      hasValue,
      invalid,
      loading,
      disabled,
      placeholder,
      selectedOptions,
      onContainerClick,
      onTriggerFocus,
      leftIcon,
    } = props;
    const valueText = getValueText();
    const totalCount = getTotalCount();
    const count = selectedOptions.length;
    const total = totalCount ?? count;
    const computedText =
      typeof valueText === 'function'
        ? valueText(count, total)
        : (valueText ??
          (count > 0 ? t('n_selected', {count, defaultValue: `${count} selected`}) : ''));

    const display = hasValue ? computedText : isOpen ? (placeholder ?? null) : null;
    const isEmpty = !hasValue;

    return (
      <button
        id={triggerId}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-labelledby={hasValue && valueId ? `${labelId} ${valueId}` : labelId}
        aria-describedby={describedErrorId}
        aria-invalid={invalid}
        aria-busy={loading}
        disabled={isBlocked}
        onClick={onContainerClick}
        onFocus={onTriggerFocus}
        className={cn(
          'relative m-0 box-border flex h-12 w-full cursor-pointer items-center justify-between gap-2 rounded-[6px] border-0 pr-4 text-left text-[16px] font-medium leading-5 outline-none transition-colors duration-200',
          leftIcon ? 'pl-10' : 'pl-4',
          isEmpty
            ? 'bg-[var(--empty-field-background)] text-[var(--chekin-color-gray-1)]'
            : 'bg-transparent text-[var(--chekin-color-brand-navy)]',
          disabled && !loading && 'cursor-not-allowed opacity-50',
          loading && '!cursor-progress',
        )}
      >
        {leftIcon && (
          <span className="pointer-events-none absolute left-0 top-0 flex h-full max-w-10 items-center justify-center text-[var(--chekin-color-gray-2)]">
            <span className="flex h-full w-10 items-center justify-center">
              {leftIcon}
            </span>
          </span>
        )}
        <span id={valueId} className="block min-w-0 flex-1 truncate text-left">
          {display}
        </span>
        <span className="pointer-events-none flex items-center gap-2 text-[var(--chekin-color-gray-2)]">
          <ChevronDown
            size={16}
            className={cn(
              'transition-transform duration-200',
              isOpen && 'rotate-180 text-[var(--chekin-color-brand-blue)]',
            )}
          />
        </span>
      </button>
    );
  }

  return CountTrigger;
}

export {createCountTrigger};
export type {CountTriggerOptions};
